import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, IconButton, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert, Tooltip, Divider,
    useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import BlockIcon from '@mui/icons-material/Block';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import api from '../../services/api';
import { isVisualizador } from '../../services/auth';
import { toastSuccess, confirmAcao } from '../../services/alerts';
import {
    STATUS_LOTE, labelStatusLote, corStatusLote, labelStatusGuia, corStatusGuia,
    formatarMoeda, formatarData,
} from '../../services/convenios';

const Indicador = ({ rotulo, valor, cor }) => (
    <Box sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
        <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: cor || 'text.primary' }}>{valor}</Typography>
    </Box>
);

export default function LoteDetalhe() {
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const soLeitura = isVisualizador();

    const [lote, setLote] = useState(null);
    const [guias, setGuias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [dialogEnvio, setDialogEnvio] = useState(false);
    const [envio, setEnvio] = useState({ dataEnvio: '', protocolo: '', vencimentoRepasse: '' });

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [l, g] = await Promise.all([
                api.get(`/lotes-faturamento/${id}`),
                api.get(`/lotes-faturamento/${id}/guias`),
            ]);
            setLote(l.data);
            setGuias(Array.isArray(g.data) ? g.data : []);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);

    const abrirDialogEnvio = () => {
        const hoje = new Date();
        const iso = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
        setEnvio({ dataEnvio: iso, protocolo: '', vencimentoRepasse: '' });
        setDialogEnvio(true);
    };

    const handleEnviar = async () => {
        setSaving(true);
        try {
            await api.patch(`/lotes-faturamento/${id}/enviar`, {
                dataEnvio: envio.dataEnvio || null,
                protocolo: envio.protocolo || null,
                vencimentoRepasse: envio.vencimentoRepasse || null,
            });
            toastSuccess('Lote enviado e título de repasse criado');
            setDialogEnvio(false);
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setSaving(false);
        }
    };

    const handleConciliar = async () => {
        const ok = await confirmAcao({
            title: 'Conciliar lote',
            text: 'A conciliação consolida pago e glosado das guias e, havendo glosa, reduz o título do repasse ao valor reconhecido. Continuar?',
            confirmButtonText: 'Conciliar',
            icon: 'question',
        });
        if (!ok) return;
        try {
            await api.patch(`/lotes-faturamento/${id}/conciliar`);
            toastSuccess('Lote conciliado');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    const handleCancelar = async () => {
        const ok = await confirmAcao({
            title: 'Cancelar lote',
            text: 'As guias voltam a ficar disponíveis para outro lote. Só é possível enquanto o lote não foi enviado.',
            confirmButtonText: 'Cancelar lote',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.patch(`/lotes-faturamento/${id}/cancelar`);
            toastSuccess('Lote cancelado');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    const handleRemoverGuia = async (guia) => {
        const ok = await confirmAcao({
            title: 'Remover guia do lote',
            text: `A guia ${guia.numeroGuia || `#${guia.id_guia_atendimento}`} volta a aparecer como faturável.`,
            confirmButtonText: 'Remover',
            icon: 'question',
        });
        if (!ok) return;
        try {
            await api.delete(`/lotes-faturamento/${id}/guias/${guia.id_guia_atendimento}`);
            toastSuccess('Guia removida do lote');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    if (loading || !lote) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    const emMontagem = lote.status === 'ABERTO';
    const enviado = lote.status === 'ENVIADO';
    const semNumero = guias.filter(g => !g.numeroGuia);

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <IconButton onClick={() => navigate('/lotes-faturamento')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
                        Lote #{lote.id_lote_faturamento}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {lote.nomePlanoSaude} • competência {formatarData(lote.competenciaInicio)} a {formatarData(lote.competenciaFim)}
                    </Typography>
                </Box>
                <Tooltip title={STATUS_LOTE[lote.status]?.ajuda || ''}>
                    <Chip label={labelStatusLote(lote.status)} color={corStatusLote(lote.status)} />
                </Tooltip>
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <Indicador rotulo="Guias" valor={lote.quantidadeGuias} />
                    <Indicador rotulo="Valor do lote" valor={formatarMoeda(lote.valorTotal)} />
                    <Indicador rotulo="Pago pela operadora" valor={formatarMoeda(lote.valorPago)} cor="success.main" />
                    <Indicador rotulo="Glosado" valor={formatarMoeda(lote.valorGlosado)} cor={Number(lote.valorGlosado) > 0 ? 'error.main' : undefined} />
                    <Indicador rotulo="Reconhecido" valor={formatarMoeda(lote.valorReconhecido)} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Envio</Typography>
                        <Typography variant="body2">{lote.dataEnvio ? formatarData(lote.dataEnvio) : 'não enviado'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Protocolo</Typography>
                        <Typography variant="body2">{lote.protocolo || '-'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Título do repasse</Typography>
                        <Typography variant="body2">
                            {lote.idContasReceber
                                ? `#${lote.idContasReceber} • ${lote.statusContasReceber} • vence ${formatarData(lote.vencimentoRepasse)}`
                                : 'criado no envio do lote'}
                        </Typography>
                    </Box>
                    {lote.observacoes && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Observações</Typography>
                            <Typography variant="body2">{lote.observacoes}</Typography>
                        </Box>
                    )}
                </Box>

                {!soLeitura && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                        {emMontagem && (
                            <Button variant="contained" startIcon={<SendIcon />} onClick={abrirDialogEnvio}>
                                Enviar ao convênio
                            </Button>
                        )}
                        {enviado && (
                            <Button variant="contained" startIcon={<DoneAllIcon />} onClick={handleConciliar}
                                disabled={lote.guiasAguardandoRetorno > 0}>
                                Conciliar
                            </Button>
                        )}
                        {emMontagem && (
                            <Button variant="outlined" color="error" startIcon={<BlockIcon />} onClick={handleCancelar}>
                                Cancelar lote
                            </Button>
                        )}
                        {lote.idContasReceber && (
                            <Button variant="text" startIcon={<ReceiptLongIcon />}
                                onClick={() => navigate(`/contas-receber/${lote.idContasReceber}`)}>
                                Abrir título do repasse
                            </Button>
                        )}
                    </Box>
                )}

                {enviado && lote.guiasAguardandoRetorno > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {lote.guiasAguardandoRetorno} guia(s) sem retorno da operadora. Registre pagamento ou glosa
                        em cada uma (tela de guias) antes de conciliar o lote.
                    </Alert>
                )}

                {emMontagem && semNumero.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {semNumero.length} guia(s) sem número da operadora. O envio é recusado até todas terem número.
                    </Alert>
                )}
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Guias do lote
                </Typography>

                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Guia</strong></TableCell>
                                <TableCell><strong>Paciente</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Atendimento</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Procedimento</strong></TableCell>
                                <TableCell align="right"><strong>Valor</strong></TableCell>
                                <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Pago</strong></TableCell>
                                <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Glosado</strong></TableCell>
                                <TableCell><strong>Situação</strong></TableCell>
                                {emMontagem && !soLeitura && <TableCell align="right"><strong>Ações</strong></TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {guias.length > 0 ? guias.map((g) => (
                                <TableRow key={g.id_guia_atendimento} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                    <TableCell>
                                        {g.numeroGuia || `#${g.id_guia_atendimento}`}
                                        {!g.numeroGuia && <Chip size="small" color="warning" sx={{ ml: 1 }} label="sem número" />}
                                    </TableCell>
                                    <TableCell>{g.nomePaciente}</TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>{formatarData(g.dataAtendimento)}</TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        <Tooltip title={g.descricaoProcedimento || ''}>
                                            <span>{g.codigoProcedimento}{g.quantidade > 1 ? ` ×${g.quantidade}` : ''}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">{formatarMoeda(g.valorTotal)}</TableCell>
                                    <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}>{formatarMoeda(g.valorPago)}</TableCell>
                                    <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        <Tooltip title={g.motivoGlosa || ''}>
                                            <span>{formatarMoeda(g.valorGlosado)}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={labelStatusGuia(g.status)} color={corStatusGuia(g.status)} />
                                    </TableCell>
                                    {emMontagem && !soLeitura && (
                                        <TableCell align="right">
                                            <Tooltip title={guias.length <= 1
                                                ? 'O lote ficaria sem guias: cancele o lote'
                                                : 'Remover do lote'}>
                                                <span>
                                                    <IconButton size="small" onClick={() => handleRemoverGuia(g)}
                                                        disabled={guias.length <= 1}>
                                                        <RemoveCircleOutlineIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                            Lote sem guias.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={dialogEnvio} onClose={() => setDialogEnvio(false)} fullWidth maxWidth="sm">
                <DialogTitle>Enviar lote ao convênio</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Alert severity="info">
                            O envio fatura todas as guias do lote e cria o título a receber do repasse. Sem vencimento
                            informado, vale a data de envio mais o prazo de repasse do convênio.
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField sx={{ flex: 1, minWidth: 160 }} type="date" label="Data de envio"
                                InputLabelProps={{ shrink: true }} value={envio.dataEnvio}
                                onChange={(e) => setEnvio(p => ({ ...p, dataEnvio: e.target.value }))}
                                helperText="Não pode ser anterior ao fim da competência." />
                            <TextField sx={{ flex: 1, minWidth: 160 }} type="date" label="Vencimento do repasse"
                                InputLabelProps={{ shrink: true }} value={envio.vencimentoRepasse}
                                onChange={(e) => setEnvio(p => ({ ...p, vencimentoRepasse: e.target.value }))} />
                        </Box>
                        <TextField label="Protocolo da operadora" value={envio.protocolo}
                            onChange={(e) => setEnvio(p => ({ ...p, protocolo: e.target.value }))}
                            inputProps={{ maxLength: 40 }}
                            helperText="Único dentro do convênio." />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogEnvio(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleEnviar} disabled={saving}>
                        {saving ? 'Enviando...' : 'Confirmar envio'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
